import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useOfferAffiliates } from "@/hooks/use-offers";
import { StatusBadge } from "@/components/StatusBadge";
import {
  Plus, Trash2, Image, Code, Video, FileText, ExternalLink,
  Pencil, X, Copy, Check, BarChart3, Link2, Mail, Archive,
} from "lucide-react";

interface Creative {
  id: number;
  name: string;
  creativeType: string;
  status: string;
  isPrivate: boolean;
  resourceUrl: string;
  htmlCode: string;
  width: number;
  height: number;
  createdAt: string;
}

interface CreativePerf {
  creative_id: number;
  clicks: number;
  impressions: number;
  conversions: number;
  revenue: number;
  payout: number;
  cr: number;
}

const typeIcons: Record<string, React.ElementType> = {
  image: Image,
  html: Code,
  video: Video,
  text: FileText,
  link: Link2,
  email: Mail,
  archive: Archive,
  thumbnail: Image,
};

const CREATIVE_TYPES = ["image", "html", "video", "text", "link", "email", "thumbnail", "archive"];

const inputCls = "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900";
const labelCls = "block text-sm font-medium text-gray-700 mb-1";

export function OfferCreativesTab({ offerId }: { offerId: string }) {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [embedId, setEmbedId] = useState<number | null>(null);
  const [showPerf, setShowPerf] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["offers", offerId, "creatives"],
    queryFn: async () => {
      const res = await api.get(`/v1/offers/${offerId}/creatives`);
      if (!res.ok) throw new Error("Failed to fetch creatives");
      return res.json();
    },
  });

  const { data: perfData } = useQuery({
    queryKey: ["offers", offerId, "creative-performance"],
    queryFn: async () => {
      const res = await api.post("/v1/reporting/creative-performance", { offerId: Number(offerId) });
      if (!res.ok) return { data: [] };
      return res.json();
    },
    enabled: showPerf,
  });

  const deleteMutation = useMutation({
    mutationFn: async (creativeId: number) => {
      const res = await api.delete(`/v1/offers/${offerId}/creatives/${creativeId}`);
      if (!res.ok) throw new Error("Failed to delete creative");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["offers", offerId, "creatives"] }),
  });

  const creatives: Creative[] = data?.data ?? [];
  const perfMap = new Map<number, CreativePerf>();
  if (perfData?.data) {
    for (const p of perfData.data as CreativePerf[]) {
      perfMap.set(p.creative_id, p);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-medium text-gray-700">{creatives.length} creative{creatives.length !== 1 ? "s" : ""}</h3>
          <button onClick={() => setShowPerf(!showPerf)}
            className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-lg border transition-colors ${showPerf ? "bg-gray-900 text-white border-gray-900" : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"}`}>
            <BarChart3 className="w-3.5 h-3.5" /> Stats
          </button>
        </div>
        <button onClick={() => { setEditingId(null); setShowForm(true); }}
          className="inline-flex items-center gap-2 bg-gray-900 text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-800">
          <Plus className="w-4 h-4" /> Add Creative
        </button>
      </div>

      {isLoading ? (
        <div className="space-y-3">{[1, 2].map((i) => <div key={i} className="h-20 bg-gray-100 rounded-xl animate-pulse" />)}</div>
      ) : creatives.length === 0 ? (
        <div className="text-center py-8 bg-white rounded-xl border border-gray-200">
          <Image className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">No creatives yet. Add banners, HTML ads, video, or text creatives for affiliates to use.</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {creatives.map((c) => {
            const Icon = typeIcons[c.creativeType] || FileText;
            const perf = perfMap.get(c.id);
            const isImage = (c.creativeType === "image" || c.creativeType === "thumbnail") && c.resourceUrl;
            return (
              <div key={c.id} className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-sm transition-shadow">
                <div className="flex items-start gap-4">
                  {/* Thumbnail */}
                  {isImage ? (
                    <div className="w-20 h-14 rounded-lg bg-gray-100 overflow-hidden flex-shrink-0 border border-gray-200">
                      <img src={c.resourceUrl} alt={c.name} className="w-full h-full object-contain" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                    </div>
                  ) : (
                    <div className="w-20 h-14 rounded-lg bg-gray-50 flex items-center justify-center flex-shrink-0 border border-gray-200">
                      <Icon className="w-6 h-6 text-gray-300" />
                    </div>
                  )}

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-gray-900 truncate">{c.name}</span>
                      {c.isPrivate && <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded font-medium">Private</span>}
                      <StatusBadge status={c.status} />
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                      <span className="capitalize">{c.creativeType}</span>
                      {c.width > 0 && c.height > 0 && <span>{c.width}x{c.height}</span>}
                      {c.resourceUrl && (
                        <a href={c.resourceUrl} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline truncate max-w-[200px]">
                          {new URL(c.resourceUrl).hostname}
                        </a>
                      )}
                    </div>

                    {showPerf && perf && (
                      <div className="flex items-center gap-4 mt-2 text-xs">
                        <span className="text-gray-500">Imps <span className="font-semibold text-gray-700">{perf.impressions.toLocaleString()}</span></span>
                        <span className="text-gray-500">Clicks <span className="font-semibold text-gray-700">{perf.clicks.toLocaleString()}</span></span>
                        <span className="text-gray-500">Conv <span className="font-semibold text-gray-700">{perf.conversions.toLocaleString()}</span></span>
                        <span className="text-gray-500">CR <span className="font-semibold text-gray-700">{perf.cr.toFixed(2)}%</span></span>
                        <span className="text-gray-500">Rev <span className="font-semibold text-green-700">${Number(perf.revenue).toFixed(2)}</span></span>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={() => setEmbedId(c.id)} title="Get embed code"
                      className="p-1.5 text-gray-400 hover:text-blue-600 rounded transition-colors">
                      <Code className="w-4 h-4" />
                    </button>
                    <button onClick={() => { setEditingId(c.id); setShowForm(true); }} title="Edit"
                      className="p-1.5 text-gray-400 hover:text-gray-600 rounded transition-colors">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => { if (window.confirm(`Delete "${c.name}"?`)) deleteMutation.mutate(c.id); }} title="Delete"
                      className="p-1.5 text-red-400 hover:text-red-600 rounded transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showForm && (
        <CreativeFormModal offerId={offerId}
          editingCreative={editingId ? creatives.find((c) => c.id === editingId) : undefined}
          onClose={() => { setShowForm(false); setEditingId(null); }} />
      )}
      {embedId !== null && (
        <EmbedCodeModal offerId={offerId} creative={creatives.find((c) => c.id === embedId)!}
          onClose={() => setEmbedId(null)} />
      )}
    </div>
  );
}

function CreativeFormModal({ offerId, editingCreative, onClose }: {
  offerId: string; editingCreative?: Creative; onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const isEdit = !!editingCreative;

  const [name, setName] = useState(editingCreative?.name ?? "");
  const [creativeType, setCreativeType] = useState(editingCreative?.creativeType ?? "image");
  const [resourceUrl, setResourceUrl] = useState(editingCreative?.resourceUrl ?? "");
  const [htmlCode, setHtmlCode] = useState(editingCreative?.htmlCode ?? "");
  const [width, setWidth] = useState(String(editingCreative?.width || ""));
  const [height, setHeight] = useState(String(editingCreative?.height || ""));
  const [status, setStatus] = useState(editingCreative?.status ?? "active");
  const [isPrivate, setIsPrivate] = useState(editingCreative?.isPrivate ?? false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const showUrlField = ["image", "video", "thumbnail", "archive", "link"].includes(creativeType);
  const showHtmlField = ["html", "email"].includes(creativeType);
  const showSizeFields = ["image", "video", "html", "thumbnail"].includes(creativeType);
  const showPreview = showUrlField && resourceUrl && (creativeType === "image" || creativeType === "thumbnail");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    const payload: Record<string, unknown> = {
      name, creativeType, status, isPrivate,
      resourceUrl: showUrlField ? resourceUrl : "",
      htmlCode: showHtmlField ? htmlCode : "",
      width: showSizeFields && width ? Number(width) : 0,
      height: showSizeFields && height ? Number(height) : 0,
    };
    try {
      const res = isEdit
        ? await api.patch(`/v1/offers/${offerId}/creatives/${editingCreative!.id}`, payload)
        : await api.post(`/v1/offers/${offerId}/creatives`, payload);
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || "Failed to save"); }
      queryClient.invalidateQueries({ queryKey: ["offers", offerId, "creatives"] });
      onClose();
    } catch (err: any) { setError(err.message); } finally { setLoading(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">{isEdit ? "Edit Creative" : "Add Creative"}</h2>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <div className="bg-red-50 text-red-700 text-sm p-3 rounded-lg border border-red-200">{error}</div>}

          <div>
            <label className={labelCls}>Name *</label>
            <input type="text" required value={name} onChange={(e) => setName(e.target.value)} className={inputCls} placeholder="e.g., Banner 728x90" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Type *</label>
              <select value={creativeType} onChange={(e) => setCreativeType(e.target.value)} className={inputCls}>
                {CREATIVE_TYPES.map((t) => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} className={inputCls}>
                <option value="active">Active</option>
                <option value="paused">Paused</option>
              </select>
            </div>
          </div>

          {showUrlField && (
            <div>
              <label className={labelCls}>Resource URL *</label>
              <input type="url" required value={resourceUrl} onChange={(e) => setResourceUrl(e.target.value)} className={inputCls}
                placeholder={creativeType === "video" ? "https://cdn.example.com/video.mp4" : "https://cdn.example.com/banner.png"} />
              {showPreview && (
                <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 p-2 max-w-full overflow-hidden">
                  <img src={resourceUrl} alt="Preview" className="max-h-32 max-w-full object-contain mx-auto"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                </div>
              )}
            </div>
          )}

          {showHtmlField && (
            <div>
              <label className={labelCls}>HTML Code *</label>
              <textarea required value={htmlCode} onChange={(e) => setHtmlCode(e.target.value)} rows={5}
                className={inputCls + " font-mono"} placeholder="<div>...</div>\n\nUse {click_url} and {impression_url} as macros." />
              <p className="text-xs text-gray-400 mt-1">Use <code className="bg-gray-100 px-1 rounded">{"{click_url}"}</code> and <code className="bg-gray-100 px-1 rounded">{"{impression_url}"}</code> as macro placeholders.</p>
            </div>
          )}

          {showSizeFields && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Width (px)</label>
                <input type="number" min="0" value={width} onChange={(e) => setWidth(e.target.value)} className={inputCls} placeholder="728" />
              </div>
              <div>
                <label className={labelCls}>Height (px)</label>
                <input type="number" min="0" value={height} onChange={(e) => setHeight(e.target.value)} className={inputCls} placeholder="90" />
              </div>
            </div>
          )}

          <label className="flex items-center gap-2">
            <input type="checkbox" checked={isPrivate} onChange={(e) => setIsPrivate(e.target.checked)} className="rounded border-gray-300" />
            <span className="text-sm text-gray-700">Private (hidden from affiliates)</span>
          </label>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50">
              {loading ? "Saving..." : isEdit ? "Save Changes" : "Create Creative"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EmbedCodeModal({ offerId, creative, onClose }: {
  offerId: string; creative: Creative; onClose: () => void;
}) {
  const { data: affData } = useOfferAffiliates(offerId);
  const assignedAffiliates = affData?.data ?? [];
  const [affiliateId, setAffiliateId] = useState("");
  const [copied, setCopied] = useState<string | null>(null);

  const { data: embedData, isLoading } = useQuery({
    queryKey: ["offers", offerId, "creatives", creative.id, "embed", affiliateId],
    queryFn: async () => {
      const qs = affiliateId ? `?affiliate_id=${affiliateId}` : "";
      const res = await api.get(`/v1/offers/${offerId}/creatives/${creative.id}/embed${qs}`);
      if (!res.ok) throw new Error("Failed to fetch embed code");
      return res.json();
    },
  });

  const embed = embedData?.data as { clickUrl: string; impressionUrl: string; htmlEmbed: string } | undefined;

  function copyToClipboard(text: string, key: string) {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Embed Code — {creative.name}</h2>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100"><X className="w-5 h-5" /></button>
        </div>

        <div className="space-y-4">
          <div>
            <label className={labelCls}>Affiliate</label>
            <select value={affiliateId} onChange={(e) => setAffiliateId(e.target.value)} className={inputCls}>
              <option value="">— Select affiliate (or leave for placeholder) —</option>
              {assignedAffiliates.map((a: any) => <option key={a.affiliateId} value={a.affiliateId}>{a.affiliateName} #{a.affiliateId}</option>)}
            </select>
          </div>

          {isLoading ? (
            <div className="h-20 bg-gray-100 rounded-xl animate-pulse" />
          ) : embed ? (
            <>
              <CodeBlock label="Click URL" value={embed.clickUrl} copied={copied === "click"} onCopy={() => copyToClipboard(embed.clickUrl, "click")} />
              <CodeBlock label="Impression Pixel URL" value={embed.impressionUrl} copied={copied === "imp"} onCopy={() => copyToClipboard(embed.impressionUrl, "imp")} />
              {embed.htmlEmbed && (
                <CodeBlock label="HTML Embed Code" value={embed.htmlEmbed} copied={copied === "html"} onCopy={() => copyToClipboard(embed.htmlEmbed, "html")} multiline />
              )}

              {(creative.creativeType === "image" || creative.creativeType === "thumbnail") && creative.resourceUrl && (
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase mb-2">Preview</label>
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                    <img src={creative.resourceUrl} alt={creative.name} className="max-h-48 max-w-full mx-auto object-contain" />
                    {creative.width > 0 && creative.height > 0 && (
                      <p className="text-xs text-gray-400 text-center mt-2">{creative.width} x {creative.height}</p>
                    )}
                  </div>
                </div>
              )}
            </>
          ) : null}
        </div>

        <div className="flex justify-end pt-4">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">Close</button>
        </div>
      </div>
    </div>
  );
}

function CodeBlock({ label, value, copied, onCopy, multiline }: {
  label: string; value: string; copied: boolean; onCopy: () => void; multiline?: boolean;
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="block text-xs font-medium text-gray-500 uppercase">{label}</label>
        <button onClick={onCopy}
          className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded transition-colors ${copied ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
          {copied ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
        </button>
      </div>
      {multiline ? (
        <pre className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs font-mono text-gray-700 overflow-x-auto whitespace-pre-wrap break-all">{value}</pre>
      ) : (
        <div className="bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-xs font-mono text-gray-700 overflow-x-auto break-all">{value}</div>
      )}
    </div>
  );
}
